const RUNTIME_SCENE = {
  DOUYIN: 'douyin_miniapp',
  WECHAT: 'wechat_miniapp',
  ECOMMERCE: 'ecommerce_web',
  UNKNOWN: 'unknown'
};

const OS_FAMILY = {
  ANDROID: 'android',
  IOS: 'ios',
  HARMONYOS: 'harmonyos',
  WINDOWS: 'windows',
  MACOS: 'macos',
  LINUX: 'linux',
  OTHER: 'other'
};

const DEVICE_CLASS = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
  OTHER: 'other'
};

const MAX_NAME_LENGTH = 80;
const DYNAMIC_SEGMENT_LENGTH = 40;
const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * 获取浏览器全局对象，SSR 或脚本探测环境下返回空对象。
 * @returns {Object} 浏览器全局对象或空对象。
 */
function getGlobalObject() {
  return typeof window !== 'undefined' ? window : {};
}

/**
 * 从 sessionStorage 中读取 JSON 数据。
 * @param {string} key - sessionStorage key。
 * @returns {Object|null} 解析后的对象，读取失败时为 null。
 */
function readSessionJson(key) {
  try {
    const globalObject = getGlobalObject();
    const storage = globalObject.sessionStorage;
    // 浏览器存储不可用时直接返回空值，避免影响业务流程。
    if (!storage) {
      return null;
    }
    const value = storage.getItem(key);
    // 目标 key 不存在时返回空值。
    if (!value) {
      return null;
    }
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

/**
 * 读取小程序上下文数据。
 * @param {string} globalKey - window 上的缓存字段。
 * @param {string} storageKey - sessionStorage 中的缓存字段。
 * @returns {Object|null} 小程序上下文。
 */
function getMiniAppData(globalKey, storageKey) {
  const globalObject = getGlobalObject();
  return globalObject[globalKey] || readSessionJson(storageKey);
}

/**
 * 判断值是否存在。
 * @param {*} value - 待判断的值。
 * @returns {boolean} 是否存在有效值。
 */
function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

/**
 * 清理低基数字段值，避免空值和过长值进入指标属性。
 * @param {*} value - 原始字段值。
 * @param {string} fallback - 兜底字段值。
 * @returns {string} 清理后的字段值。
 */
function sanitizeNamePart(value, fallback) {
  const text = String(hasValue(value) ? value : fallback || 'unknown')
    .trim()
    .replace(/[^a-zA-Z0-9_/.-]+/g, '_');
  return text.slice(0, MAX_NAME_LENGTH) || fallback || 'unknown';
}

/**
 * 识别当前运行场景。
 * @returns {string} 运行场景枚举。
 */
function resolveRuntimeScene() {
  const dyData = getMiniAppData('_dyData', 'dyData');
  // 抖音订单号存在时优先判定为抖音小程序场景。
  if (dyData && dyData.orderNo) {
    return RUNTIME_SCENE.DOUYIN;
  }
  const wxData = getMiniAppData('_wxData', 'wxData');
  // 微信上下文存在时判定为微信小程序场景。
  if (wxData && (wxData.token || wxData.orderNo || wxData.uid)) {
    return RUNTIME_SCENE.WECHAT;
  }
  const globalObject = getGlobalObject();
  // 浏览器 location 可用时默认归为普通电商 Web 场景。
  if (globalObject.location) {
    return RUNTIME_SCENE.ECOMMERCE;
  }
  return RUNTIME_SCENE.UNKNOWN;
}

/**
 * 获取 User-Agent 字符串。
 * @param {string} [userAgent] - 外部传入的 User-Agent。
 * @returns {string} User-Agent 字符串。
 */
function resolveUserAgent(userAgent) {
  const globalObject = getGlobalObject();
  const navigatorObject = globalObject.navigator || {};
  return String(userAgent || navigatorObject.userAgent || '');
}

/**
 * 识别操作系统族。
 * @param {string} [userAgent] - User-Agent。
 * @returns {string} 操作系统族枚举。
 */
export function resolveOsFamily(userAgent) {
  const ua = resolveUserAgent(userAgent);
  // HarmonyOS UA 常同时包含 Android，必须优先识别。
  if (/harmonyos|hmos/i.test(ua)) {
    return OS_FAMILY.HARMONYOS;
  }
  // Android 系统识别。
  if (/android/i.test(ua)) {
    return OS_FAMILY.ANDROID;
  }
  // iOS 系统识别。
  if (/iphone|ipad|ipod|ios/i.test(ua)) {
    return OS_FAMILY.IOS;
  }
  // Windows 桌面系统识别。
  if (/windows/i.test(ua)) {
    return OS_FAMILY.WINDOWS;
  }
  // macOS 桌面系统识别。
  if (/mac os|macintosh/i.test(ua)) {
    return OS_FAMILY.MACOS;
  }
  // Linux 桌面系统识别。
  if (/linux/i.test(ua)) {
    return OS_FAMILY.LINUX;
  }
  return OS_FAMILY.OTHER;
}

/**
 * 识别设备类型。
 * @param {string} [userAgent] - User-Agent。
 * @returns {string} 设备类型枚举。
 */
export function resolveDeviceClass(userAgent) {
  const ua = resolveUserAgent(userAgent);
  // 平板设备优先于普通移动设备识别。
  if (/ipad|tablet|pad/i.test(ua)) {
    return DEVICE_CLASS.TABLET;
  }
  // 移动设备识别。
  if (/mobile|iphone|ipod|android/i.test(ua)) {
    return DEVICE_CLASS.MOBILE;
  }
  // 桌面设备识别。
  if (/windows|macintosh|mac os|linux/i.test(ua)) {
    return DEVICE_CLASS.DESKTOP;
  }
  return DEVICE_CLASS.OTHER;
}

/**
 * 解析当前运行上下文。
 * @returns {{runtimeScene:string,osFamily:string,deviceClass:string}} 低基数运行上下文。
 */
export function resolveRuntimeContext() {
  return {
    runtimeScene: resolveRuntimeScene(),
    osFamily: resolveOsFamily(),
    deviceClass: resolveDeviceClass()
  };
}

/**
 * 规范化 Vue Router 路由名。
 * @param {Object|string} route - Vue Router 路由对象或路由名。
 * @returns {string} 规范化路由名。
 */
export function normalizeRouteName(route) {
  const name = typeof route === 'string' ? route : route && route.name;
  return sanitizeNamePart(name, 'unknown_route');
}

/**
 * 判断 URL 路径段是否为动态值。
 * @param {string} segment - URL 路径段。
 * @returns {boolean} 是否为动态路径段。
 */
function isDynamicSegment(segment) {
  return (
    /^\d+$/.test(segment) ||
    UUID_SEGMENT_RE.test(segment) ||
    segment.length > DYNAMIC_SEGMENT_LENGTH
  );
}

/**
 * 规范化接口名称。
 * @param {string} url - 请求 URL。
 * @returns {string} 去掉 origin、query、hash 和动态段后的接口名称。
 */
export function normalizeApiName(url) {
  try {
    const globalObject = getGlobalObject();
    const base =
      (globalObject.location && globalObject.location.origin) ||
      'https://h5.local';
    const parsed = new URL(String(url || ''), base);
    const segments = parsed.pathname.split('/').map((segment) => {
      return isDynamicSegment(segment) ? ':id' : sanitizeNamePart(segment, '');
    });
    const path = segments.join('/').replace(/\/+/g, '/');
    return path || '/';
  } catch (error) {
    return '/unknown';
  }
}

/**
 * 按固定采样率返回本次请求是否采样。
 * @param {number} rate - 采样率，取值 0 到 1。
 * @returns {boolean} 是否采样。
 */
export function shouldSample(rate) {
  const numericRate = Number(rate);
  // 非法采样率按不采样处理。
  if (!Number.isFinite(numericRate) || numericRate <= 0) {
    return false;
  }
  // 大于等于 1 的采样率按全量采样处理。
  if (numericRate >= 1) {
    return true;
  }
  return Math.random() < numericRate;
}

export { RUNTIME_SCENE, OS_FAMILY, DEVICE_CLASS };
